<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-justify-between">
    <div class="tw-flex tw-flex-col tw-gap-6 tw-mb-6">
      <div class="tw-text-sf-gray tw-font-medium tw-text-lg tw-leading-[1.4]">
        {{ I18n.t('external_links.manager.title') }}
      </div>
      <SfInput
        v-model="url"
        :label="I18n.t('external_links.manager.labels.link')"
        :placeholder="I18n.t('external_links.manager.placeholders.link')"
        class="tw-w-full"
        :required="true"
        :error-text="
          url !== '' && !isLinkWithSfAllowedProtocol(url)
            ? I18n.t('external_links.manager.errors.invalid_url')
            : ''
        "
        data-test-id="external-link-url-input"
      />
      <SfInput
        v-model="name"
        :label="I18n.t('external_links.manager.labels.name')"
        :placeholder="I18n.t('external_links.manager.placeholders.name')"
        class="tw-w-full"
        :required="true"
        data-test-id="external-link-name-input"
      />
      <div
        class="
          tw-bg-sf-neutral-subtle-2
          tw-p-4
          tw-rounded-lg
          tw-text-sm
          tw-leading-[1.4]
        "
      >
        <div class="tw-text-sf-gray tw-font-medium">
          {{ I18n.t('external_links.manager.limitations.title') }}
        </div>
        <div class="tw-text-sf-gray-dark">
          {{ I18n.t('external_links.manager.limitations.description') }}
        </div>
        <ul class="!tw-list-disc tw-text-sf-gray-dark !tw-mb-4 !tw-ml-6">
          <li>
            {{ I18n.t('external_links.manager.limitations.point1') }}
          </li>
          <li>
            {{ I18n.t('external_links.manager.limitations.point2') }}
          </li>
          <li>
            {{ I18n.t('external_links.manager.limitations.point3') }}
          </li>
          <li>
            {{ I18n.t('external_links.manager.limitations.point4') }}
          </li>
        </ul>
        <SfBannerInline :variant="'info'">
          <template #title>
            {{ I18n.t('external_links.manager.limitations.footer') }}
          </template>
        </SfBannerInline>
      </div>
    </div>
    <div class="tw-flex tw-justify-center tw-gap-6 tw-items-center">
      <SfButtonDs variant="secondary" @click="handleCancel">
        {{ I18n.t('external_links.manager.cancel') }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="!isReadyToAddLink"
        @click="handleAddLink"
      >
        {{ saveButtonText }}
      </SfButtonDs>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import { SfButtonDs, SfInput } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { isLinkWithSfAllowedProtocol } from '@app/utils/url-helpers'

interface Props {
  name?: string
  url?: string
  id?: number
}

const {
  name: initialName = '',
  url: initialUrl = '',
  id: initialId = 0,
} = defineProps<Props>()

const emit =
  defineEmits<{
    cancel: []
    'add-link': [
      {
        url: string
        name: string
        id?: number
      }
    ]
  }>()

const I18n = useI18n()

const url = ref(initialUrl || '')
const name = ref(initialName || '')
const id = ref(initialId || 0)

function handleAddLink() {
  const payload = {
    url: url.value,
    name: name.value,
    // Only include id for existing links (when editing)
    id: id.value > 0 ? id.value : undefined,
  }

  emit('add-link', payload)
}

function handleCancel() {
  emit('cancel')
}

const isValidLink = computed(() => {
  if (!url.value) {
    return false
  }

  return isLinkWithSfAllowedProtocol(url.value)
})

const isValidName = computed(() => {
  return name.value.trim().length > 2
})

const isReadyToAddLink = computed(() => {
  return isValidLink.value && isValidName.value
})

const saveButtonText = computed(() =>
  id.value
    ? I18n.t('external_links.manager.change')
    : I18n.t('external_links.manager.attach')
)
</script>
